import type {
  APIConfig,
  BigbrotherClient,
  CinnamonClient,
  CinnamonWebSocketFactory,
  DerekClient,
  HotelClient,
  Logger,
} from '@/apis/infrastructures'
import type { Container, interfaces } from 'inversify'
import { ContainerModule } from 'inversify'
import { APIConfigProvider } from './config'
import {
  createBigbrotherClient,
  createCinnamonClient,
  createDerekClient,
  createHotelClient,
  createLogger,
} from './restClients'
import { TYPES } from './types'
import { createCinnamonWebSocketFactory } from './websocketFactory'

const bindAll = (
  apiConfig: ReturnType<typeof APIConfigProvider>,
  bind: interfaces.Bind
) => {
  bind<APIConfig>(TYPES.APIConfig).toConstantValue(apiConfig)
  bind<DerekClient>(TYPES.DerekClient).toConstantValue(
    createDerekClient(apiConfig)
  )
  bind<CinnamonClient>(TYPES.CinnamonClient).toConstantValue(
    createCinnamonClient(apiConfig)
  )
  bind<CinnamonWebSocketFactory>(
    TYPES.CinnamonWebSocketFactory
  ).toConstantValue(createCinnamonWebSocketFactory(apiConfig))
  bind<Logger>(TYPES.Logger).toConstantValue(createLogger(apiConfig))
  bind<BigbrotherClient>(TYPES.BigbrotherClient).toConstantValue(
    createBigbrotherClient(apiConfig)
  )
  bind<HotelClient>(TYPES.HotelClient).toConstantValue(
    createHotelClient(apiConfig)
  )
}

export const infrastructures = new ContainerModule((bind: interfaces.Bind) => {
  const apiConfig = APIConfigProvider()
  bindAll(apiConfig, bind)
})

export const rebindAPIConfig = (container: Container, apiToken?: APIToken) => {
  const apiConfig = APIConfigProvider(apiToken)
  if (container.isBound(TYPES.APIConfig)) {
    container.rebind<APIConfig>(TYPES.APIConfig).toConstantValue(apiConfig)
    container
      .rebind<DerekClient>(TYPES.DerekClient)
      .toConstantValue(createDerekClient(apiConfig))
    container
      .rebind<CinnamonClient>(TYPES.CinnamonClient)
      .toConstantValue(createCinnamonClient(apiConfig))
    container
      .rebind<CinnamonWebSocketFactory>(TYPES.CinnamonWebSocketFactory)
      .toConstantValue(createCinnamonWebSocketFactory(apiConfig))
    container
      .rebind<Logger>(TYPES.Logger)
      .toConstantValue(createLogger(apiConfig))
    container
      .rebind<BigbrotherClient>(TYPES.BigbrotherClient)
      .toConstantValue(createBigbrotherClient(apiConfig))
    container
      .rebind<HotelClient>(TYPES.HotelClient)
      .toConstantValue(createHotelClient(apiConfig))
    return
  }
  bindAll(apiConfig, container.bind)
}

export const clearAPIConfig = (container: Container): void => {
  rebindAPIConfig(container)
}
